import { createAsyncThunk } from '@reduxjs/toolkit';
import { SnakeCasedPropertiesDeep } from 'type-fest';

import { RootState } from '../../redux/root-reducer';
import { endpoints } from '../../utils/endpoints';
import { fetchJSON } from '../../utils/fetch';
import { logError } from '../../utils/logging';
import {
  SeriesEventWidget,
  SeriesTicketType,
} from '../event-details/event-detail-types';
import { QuestionTypeWithBankId } from '../registration-form/custom-form-factory-types';
import { getSeriesPromoCode } from './series-cart-selectors';
import {
  calculateSeriesOrderSummary,
  validateSeriesPromoCode,
} from './series-cart-thunk';
import {
  getSeriesSelectedEvents,
  getSeriesSelectedTickets,
} from './series-selectors';

const get = endpoints();

type SeriesDataParams = {
  slug: string;
  seriesExternalId: string | null;
};

type SeriesDefaultWidgetResponse = {
  active_series_events_widgets: SnakeCasedPropertiesDeep<SeriesEventWidget>[];
  active_series: string | null;
  event_series: Record<string, string>;
};

export const loadSeriesDefaultWidget = createAsyncThunk<
  SeriesDefaultWidgetResponse,
  SeriesDataParams,
  { state: RootState }
>(
  'series/loadSeriesDefaultWidget',
  async ({ seriesExternalId, slug }, thunkAPI) => {
    const seriesDefaultWidgetURL = get(`SERIES_EVENT_DETAILS`)(
      slug,
      seriesExternalId,
    );

    try {
      const response: SeriesDefaultWidgetResponse = await fetchJSON(
        seriesDefaultWidgetURL,
      );

      thunkAPI.dispatch(
        loadWidgetTicketsThunk({
          eventExternalIds: response.active_series_events_widgets.map(
            widget => widget.event.external_id,
          ),
        }),
      );

      return response;
    } catch (error) {
      logError(error as any, { location: 'Redux Series slice' });
      return thunkAPI.rejectWithValue('Failed to fetch event series data');
    }
  },
);

type WidgetTicketsResponse = Record<
  string,
  SnakeCasedPropertiesDeep<SeriesTicketType[]>
>;

export const loadWidgetTicketsThunk = createAsyncThunk<
  WidgetTicketsResponse,
  { eventExternalIds: string[] },
  { state: RootState }
>('series/loadWidgetTickets', async ({ eventExternalIds }, thunkAPI) => {
  try {
    const url = get(`WIDGET_TICKETS`)(eventExternalIds);

    const response: WidgetTicketsResponse = await fetchJSON(url);

    return response;
  } catch (error) {
    logError(error as any, { location: 'Redux Series slice' });
    return thunkAPI.rejectWithValue('Failed to fetch event tickets data');
  }
});

type WidgetQuestionsResponse = Record<string, QuestionTypeWithBankId[]>;

export const loadWidgetQuestionsThunk = createAsyncThunk<
  WidgetQuestionsResponse,
  { ticketExternalIds: Record<string, string> },
  { state: RootState }
>('series/loadWidgetQuestions', async ({ ticketExternalIds }, thunkAPI) => {
  try {
    const responses = await Promise.all(
      Object.entries(ticketExternalIds).map(
        async ([eventExternalId, ticketExternalId]) => {
          const url = get(`WIDGET_QUESTIONS`)(ticketExternalId);
          const data = await fetchJSON(url);

          return { [eventExternalId]: data?.questions };
        },
      ),
    );

    const combinedResponses = responses.reduce((acc, response) => {
      return { ...acc, ...response };
    }, {});

    return combinedResponses;
  } catch (error) {
    logError(error as any, { location: 'Redux Series slice' });
    return thunkAPI.rejectWithValue('Failed to fetch event questions data');
  }
});

export const calculateSeriesOrderThunk = createAsyncThunk<
  void,
  {
    eventSlug: string;
    source?: string;
    eventId?: string;
    promoCodeEnabled?: boolean;
    skipPromoCodeValidation?: boolean;
  },
  { state: RootState }
>(
  'series/calculateSeriesOrder',
  async (
    { eventSlug, source, eventId, promoCodeEnabled, skipPromoCodeValidation },
    { dispatch, getState, rejectWithValue },
  ): Promise<any> => {
    const state = getState();
    const selectedEvents = getSeriesSelectedEvents(state);
    const selectedTicketIds = getSeriesSelectedTickets(state);
    const selectedTicketsEventIds = Object.keys(selectedTicketIds);

    // If the user toggles an event checkbox and there are no tickets selected for that event, don't calculate the order
    if (
      source === 'toggle-event-checkbox' &&
      eventId &&
      !selectedTicketsEventIds.includes(eventId)
    ) {
      return;
    }

    // If the user unchecks all events and there are no tickets in the cart, don't calculate the order
    if (
      source === 'uncheck-all' &&
      Object.keys(state.seriesCart.selectedTicketOrderSummary).length === 0
    ) {
      return;
    }

    let tickets = Object.entries(selectedTicketIds)
      .filter(([_, value]) => !!value)
      .map(([eventId, ticketExternalId]) => ({
        ticketExternalId: selectedEvents[eventId]?.isSelected
          ? ticketExternalId
          : '',
        orderId:
          state.seriesCart.selectedTicketOrderSummary[
            eventId
          ]?.id?.toString() || '',
        eventId,
      }));

    // filter out tickets whose events are not selected
    tickets = tickets.filter(
      ticket => !!selectedEvents[ticket.eventId]?.isSelected,
    );

    // If there is a promo code applied and the flag is enabled, re-validate the promo code
    if (promoCodeEnabled && !skipPromoCodeValidation) {
      const promoCode = getSeriesPromoCode(state);
      if (promoCode) {
        await dispatch(
          validateSeriesPromoCode({
            promoCode,
            selectedTicketIds: tickets.map(ticket => ticket.ticketExternalId),
          }),
        );
      }
    }

    try {
      return await dispatch(
        calculateSeriesOrderSummary({
          eventSlug,
          eventSeriesExternalId: state.series.activeSeries || '',
          tickets,
          seriesOrderExternalId: state.seriesCart.seriesOrderExternalId || '',
          email: state.series.questions.userDetails?.email || '',
        }),
      ).unwrap();
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
